import CtaIcon from '../ui/CtaIcon';
import React from 'react';
import type { AdCreative } from './homeTypes';
import { useSponsoredImpression } from './useSponsoredImpression';
import apiService from '../../services/ApiService';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import Button from '../ui/Button';

interface SponsoredPlacementProps {
  creative: AdCreative;
  variant?: 'banner' | 'card';
}

export const SponsoredPlacement: React.FC<SponsoredPlacementProps> = ({
  creative,
  variant = 'banner',
}) => {
  const ref = useSponsoredImpression(creative);
  const imageUrl = resolvePublicFileUrl(creative.image_url);
  const clickHref = creative.click_url || apiService.buildAdClickUrl(creative);

  const onClick = () => {
    void apiService.trackEvent('ad_click', {
      metadata: {
        creative_id: creative.creative_id,
        campaign_id: creative.campaign_id,
        placement: creative.placement,
      },
    });
  };

  if (variant === 'card') {
    return (
      <aside
        ref={ref as React.RefObject<HTMLElement>}
        className="rounded-lg border border-white/[0.06] bg-[#0c0c0c]/70 overflow-hidden"
        aria-label={`Sponsored by ${creative.advertiser_name}`}
      >
        <p className="px-3 pt-3 text-[11px] uppercase tracking-[0.12em] text-gray-500">
          Sponsored · {creative.advertiser_name}
        </p>
        {imageUrl ? (
          <a href={clickHref} onClick={onClick} className="block aspect-[16/9] bg-[#0a0a0a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400">
            <img
              src={imageUrl}
              alt={creative.alt_text || creative.headline}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </a>
        ) : null}
        <div className="p-4">
          <h3 className="text-white font-medium mb-1">{creative.headline}</h3>
          {creative.body ? <p className="text-gray-400 text-sm mb-3 line-clamp-2">{creative.body}</p> : null}
          <a
            href={clickHref}
            onClick={onClick}
            className="inline-flex text-sm text-orange-300 hover:text-orange-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 rounded"
          >
            {creative.cta_label || 'View details'} <CtaIcon direction="next" />
          </a>
        </div>
      </aside>
    );
  }

  return (
    <aside
      ref={ref as React.RefObject<HTMLElement>}
      className="py-6 sm:py-8"
      aria-label={`Sponsored by ${creative.advertiser_name}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-stretch sm:items-center border border-white/[0.06] bg-[#0c0c0c]/50 px-4 py-4 sm:px-6 sm:py-5">
          {imageUrl ? (
            <a
              href={clickHref}
              onClick={onClick}
              className="block w-full sm:w-40 aspect-[16/10] flex-shrink-0 overflow-hidden bg-[#0a0a0a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
            >
              <img
                src={imageUrl}
                alt={creative.alt_text || ''}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </a>
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500 mb-1">
              Sponsored · {creative.advertiser_name}
            </p>
            <h3 className="text-white font-semibold text-lg mb-1">{creative.headline}</h3>
            {creative.body ? (
              <p className="text-gray-400 text-sm leading-relaxed line-clamp-2">{creative.body}</p>
            ) : null}
          </div>
          <div className="flex-shrink-0">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                onClick();
                window.location.assign(clickHref);
              }}
            >
              {creative.cta_label || 'View details'}
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default SponsoredPlacement;
